import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Calendar } from '../components/ui/Calendar';
import { Badge } from '../components/ui/Badge';
import { ArrowLeft, Calendar as CalendarIcon, Briefcase, CheckCircle2 } from 'lucide-react';
import { dates } from '../utils/dates';
interface CalendarEvent {
  date: Date;
  title: string;
  type: 'project' | 'deliverable' | 'task';
  status: 'upcoming' | 'today' | 'overdue';
}
const mockEvents: CalendarEvent[] = [{
  date: new Date(dates.deliverableDeadline1),
  title: 'Nike Post Deliverable',
  type: 'deliverable',
  status: 'upcoming'
}, {
  date: new Date(dates.deliverableDeadline2),
  title: 'Nike Story Deliverable',
  type: 'deliverable',
  status: 'upcoming'
}, {
  date: new Date(dates.taskDeadline1),
  title: 'Review Nike Campaign',
  type: 'task',
  status: 'upcoming'
}, {
  date: new Date(dates.activeDeadline1),
  title: 'Nike Summer Campaign',
  type: 'project',
  status: 'upcoming'
}, {
  date: new Date(dates.activeDeadline2),
  title: 'Adidas Fitness Series',
  type: 'project',
  status: 'upcoming'
}];
export function CalendarPage() {
  const navigate = useNavigate();
  const [selectedDates, setSelectedDates] = useState<Date[] | undefined>(mockEvents.map(e => e.date));
  const today = new Date();
  const upcomingEvents = mockEvents.filter(e => e.date >= today).sort((a, b) => a.date.getTime() - b.date.getTime()).slice(0, 5);
  return <div className="min-h-screen bg-gray-50 pb-12" data-id="element-427">
      <header className="bg-white border-b sticky top-0 z-10" data-id="element-428">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-429">
          <div className="flex items-center" data-id="element-430">
            <Button variant="ghost" size="sm" className="mr-4" onClick={() => navigate('/manager')} data-id="element-431">
              <ArrowLeft className="h-4 w-4 mr-2" data-id="element-432" /> Back
            </Button>
            <h1 className="text-xl font-bold text-gray-900" data-id="element-433">Calendar</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-434">
          <div className="flex space-x-8 -mb-px" data-id="element-435">
            <button onClick={() => navigate('/manager')} className="pb-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent" data-id="element-436">
              Overview
            </button>
            <button onClick={() => navigate('/manager/creators')} className="pb-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent" data-id="element-437">
              Creators
            </button>
            <button onClick={() => navigate('/manager/brands')} className="pb-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent" data-id="element-438">
              Brands
            </button>
            <button onClick={() => navigate('/manager/projects')} className="pb-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent" data-id="element-439">
              Projects
            </button>
            <button onClick={() => navigate('/manager/tasks')} className="pb-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent" data-id="element-440">
              Tasks
            </button>
            <button className="pb-4 text-sm font-medium text-blue-600 border-b-2 border-blue-600" data-id="element-441">
              Calendar
            </button>
            <button onClick={() => navigate('/manager/platforms')} className="pb-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent" data-id="element-442">
              Platforms
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-443">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-444">
          {/* Calendar */}
          <div className="lg:col-span-2" data-id="element-445">
            <Card data-id="element-446">
              <CardHeader data-id="element-447">
                <CardTitle className="flex items-center" data-id="element-448">
                  <CalendarIcon className="h-5 w-5 mr-2" data-id="element-449" />
                  Deadlines & Events
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-450">
                <Calendar mode="multiple" selected={selectedDates} onSelect={setSelectedDates} className="rounded-md border" data-id="element-451" />
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Events */}
          <div className="lg:col-span-1" data-id="element-452">
            <Card data-id="element-453">
              <CardHeader data-id="element-454">
                <CardTitle data-id="element-455">Upcoming Deadlines</CardTitle>
              </CardHeader>
              <CardContent data-id="element-456">
                <div className="space-y-4" data-id="element-457">
                  {upcomingEvents.map((event, index) => {
                  const daysUntil = Math.ceil((event.date.getTime() - today.getTime()) / (1000 * 3600 * 24));
                  const isUrgent = daysUntil < 7;
                  return <div key={index} className="flex items-start space-x-3 pb-4 border-b last:border-0" data-id="element-458">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${event.type === 'project' ? 'bg-blue-100' : event.type === 'deliverable' ? 'bg-purple-100' : 'bg-green-100'}`} data-id="element-459">
                          {event.type === 'project' ? <Briefcase className="h-5 w-5 text-blue-600" data-id="element-460" /> : event.type === 'deliverable' ? <CheckCircle2 className="h-5 w-5 text-purple-600" data-id="element-461" /> : <CalendarIcon className="h-5 w-5 text-green-600" data-id="element-462" />}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-463">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-464">
                            {event.title}
                          </p>
                          <p className={`text-xs ${isUrgent ? 'text-red-600 font-medium' : 'text-gray-500'}`} data-id="element-465">
                            {event.date.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                            {daysUntil === 0 && ' (Today)'}
                            {daysUntil > 0 && ` (${daysUntil} days)`}
                          </p>
                          <Badge variant={event.type === 'project' ? 'default' : event.type === 'deliverable' ? 'secondary' : 'success'} className="mt-1 text-xs capitalize" data-id="element-466">
                            {event.type}
                          </Badge>
                        </div>
                      </div>;
                })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>;
}